import styled from '@emotion/styled';
import { cx } from '@emotion/css';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import identity from 'ramda/src/identity';
import React, { useMemo } from 'react';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';
import { useFilters, usePagination, useSortBy, useTable } from 'react-table';

import hasLength from '../../utils/hasLength';
import TherigyLoadingSpinner from '../TherigyLoadingSpinner/TherigyLoadingSpinner';
import SearchColumnFilter from './SearchColumnFilter';
import TableMessage from './TableMessage';
import TablePagination from './TablePagination';

const noop = () => {};

const propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({})),
    stateReducer: PropTypes.func,
    isLoading: PropTypes.bool,
    placeholderText: PropTypes.string,
    initialState: PropTypes.shape({}),
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    enablePagination: PropTypes.bool,
    getKey: PropTypes.func,
    filterTypes: PropTypes.arrayOf(PropTypes.shape({})),
    className: PropTypes.string,
};

const defaultProps = {
    data: [],
    stateReducer: identity,
    isLoading: false,
    initialState: {},
    pageSizeOptions: [10, 25, 50],
    enablePagination: true,
    placeholderText: undefined,
    getKey: undefined,
    filterTypes: undefined,
    className: '',
};

const FilterRowStyle = styled('tr')`
    background-color: #fff;
`;

const FlexHeaderContents = styled('div')`
    display: flex;
    align-items: center;
    justify-content: flex-start;

    .header-text {
        margin-right: 5px;
    }

    .sort-indicator-container {
        display: flex;
        align-items: center;
    }

    .sort-indicator {
        opacity: 0.4;

        &:hover {
            opacity: 1;
        }
    }
`;

const Table = ({
    columns,
    data,
    stateReducer,
    isLoading,
    placeholderText,
    getKey,
    filterTypes,
    initialState,
    pageSizeOptions,
    enablePagination,
    className,
}) => {
    const defaultColumn = useMemo(
        () => ({
            // Set up our default Filter UI
            Filter: SearchColumnFilter,
        }),
        []
    );
    const plugins = enablePagination ? [useFilters, useSortBy, usePagination] : [useFilters, useSortBy];
    const hasFilter = useMemo(() => !columns.every((column) => column.disableFilters), [columns]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page, // Instead of using 'rows', we'll use page,
        // which has only the rows for the active page when pagination enabled
        rows,

        // The rest of these things are super handy, too.
        canPreviousPage,
        canNextPage,
        pageOptions,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn,
            stateReducer,
            filterTypes,
            initialState,
        },
        ...plugins
    );

    const records = useMemo(() => {
        return enablePagination ? page : rows;
    }, [enablePagination, page, rows]);

    return (
        <div>
            <table {...getTableProps()} className={`therigy-table ${className}`}>
                <thead>
                    {headerGroups.map((headerGroup) => {
                        return (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => {
                                    const isSortable = column.canSort && !column.disableSort;
                                    const headerProps = column.getHeaderProps(column.getSortByToggleProps());
                                    const label = typeof column.Header === 'string' ? column.Header : column.id;
                                    return (
                                        <th
                                            {...headerProps}
                                            onClick={isSortable ? headerProps.onClick : noop}
                                            title={isSortable ? headerProps.title : null}
                                            style={column.style}
                                            aria-label={isSortable ? `Sort by ${label}` : label}
                                            className={cx({
                                                sortable: isSortable,
                                                'sort-asc': column.isSorted && !column.isSortedDesc,
                                                'sort-desc': column.isSorted && column.isSortedDesc,
                                            })}
                                        >
                                            <FlexHeaderContents>
                                                <span className="header-text">{column.render('Header')}</span>
                                                {isSortable && (
                                                    <span className="sort-indicator-container">
                                                        {
                                                            // eslint-disable-next-line no-nested-ternary
                                                            column.isSorted ? (
                                                                column.isSortedDesc ? (
                                                                    <FaSortDown className="sort-indicator" />
                                                                ) : (
                                                                    <FaSortUp className="sort-indicator" />
                                                                )
                                                            ) : (
                                                                <FaSort className="sort-indicator" />
                                                            )
                                                        }
                                                    </span>
                                                )}
                                            </FlexHeaderContents>
                                            {column.SubHeader && (
                                                <small className="text-muted">{column.render('SubHeader')}</small>
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}

                    {hasFilter &&
                        headerGroups.map((headerGroup) => (
                            <FilterRowStyle {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => {
                                    return (
                                        <td {...column.getHeaderProps()}>
                                            <div>{column.canFilter ? column.render('Filter') : null}</div>
                                        </td>
                                    );
                                })}
                            </FilterRowStyle>
                        ))}
                </thead>

                <tbody {...getTableBodyProps()}>
                    {
                        // eslint-disable-next-line no-nested-ternary
                        isLoading ? (
                            <TableMessage>
                                <TherigyLoadingSpinner />
                            </TableMessage>
                        ) : hasLength(records) ? (
                            records.map((row) => {
                                prepareRow(row);
                                const props = row.getRowProps();
                                if (getKey && getKey(row.original)) {
                                    props.key = getKey(row.original);
                                }
                                return (
                                    <tr {...props}>
                                        {row.cells.map((cell) => {
                                            return (
                                                <td
                                                    {...cell.getCellProps()}
                                                    data-testid={
                                                        typeof cell.column.Header !== 'string'
                                                            ? `cell-${cell.column.id}`
                                                            : `cell-${cell.column.Header}`
                                                    }
                                                >
                                                    {cell.column.fallbackToDashDash && R.isNil(cell.value)
                                                        ? '--'
                                                        : cell.render('Cell')}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })
                        ) : (
                            <TableMessage message={placeholderText} />
                        )
                    }
                </tbody>
            </table>
            {enablePagination && (
                <TablePagination
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    previousPage={previousPage}
                    nextPage={nextPage}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    gotoPage={gotoPage}
                    pageIndex={pageIndex}
                    pageOptions={pageOptions}
                    pageSizeOptions={pageSizeOptions}
                />
            )}
        </div>
    );
};

Table.propTypes = propTypes;
Table.defaultProps = defaultProps;

export default Table;
